import { Mic, Mic2, Sliders, Film, Palette, Shield } from 'lucide-react';

/**
 * SenritsuX Role-Based Feature Access
 *
 * Each role maps to a set of features the user can access.
 * Users with multiple roles get the UNION of all their roles' features.
 */

export const ROLES = {
  SINGER: 'singer',
  RAPPER: 'rapper',
  PRODUCER: 'producer',
  VIDEO_EDITOR: 'video_editor',
  GRAPHIC_DESIGNER: 'graphic_designer',
  ADMIN: 'admin',
};

export const FEATURES = {
  DISTRIBUTION: 'distribution',
  ARTIST_WEB: 'artist_web',
  FIND_COLLAB: 'find_collab',
  BEAT_STORE: 'beat_store',
  BID_MARKETPLACE: 'bid_marketplace',
  PORTFOLIO: 'portfolio',
  DASHBOARD: 'dashboard',
  CREATOR_TOOLS: 'creator_tools',
  EDIT_PROFILE: 'edit_profile',
  FOLLOWERS_LISTENERS: 'followers_listeners',
  DRAFTS: 'drafts',
  PRICING_PLAN: 'pricing_plan',
  ADMIN_PANEL: 'admin_panel',
};

/** Role → allowed features mapping */
export const ROLE_FEATURES = {
  [ROLES.SINGER]: [
    FEATURES.DISTRIBUTION,
    FEATURES.ARTIST_WEB,
    FEATURES.FIND_COLLAB,
    FEATURES.FOLLOWERS_LISTENERS,
    FEATURES.DRAFTS,
    FEATURES.DASHBOARD,
    FEATURES.PRICING_PLAN,
    FEATURES.CREATOR_TOOLS,
    FEATURES.EDIT_PROFILE,
    FEATURES.BEAT_STORE,
    FEATURES.BID_MARKETPLACE,
    FEATURES.PORTFOLIO,
  ],
  [ROLES.RAPPER]: [
    FEATURES.DISTRIBUTION,
    FEATURES.ARTIST_WEB,
    FEATURES.FIND_COLLAB,
    FEATURES.FOLLOWERS_LISTENERS,
    FEATURES.DRAFTS,
    FEATURES.DASHBOARD,
    FEATURES.CREATOR_TOOLS,
    FEATURES.EDIT_PROFILE,
    FEATURES.PORTFOLIO,
  ],
  [ROLES.PRODUCER]: [
    FEATURES.BEAT_STORE,
    FEATURES.BID_MARKETPLACE,
    FEATURES.FOLLOWERS_LISTENERS,
    FEATURES.DRAFTS,
    FEATURES.DASHBOARD,
    FEATURES.CREATOR_TOOLS,
    FEATURES.EDIT_PROFILE,
    FEATURES.PORTFOLIO,
    FEATURES.ARTIST_WEB,
  ],
  [ROLES.VIDEO_EDITOR]: [
    FEATURES.BID_MARKETPLACE,
    FEATURES.PORTFOLIO,
    FEATURES.DASHBOARD,
    FEATURES.CREATOR_TOOLS,
    FEATURES.EDIT_PROFILE,
  ],
  [ROLES.GRAPHIC_DESIGNER]: [
    FEATURES.BID_MARKETPLACE,
    FEATURES.PORTFOLIO,
    FEATURES.DASHBOARD,
    FEATURES.CREATOR_TOOLS,
    FEATURES.EDIT_PROFILE,
  ],
  [ROLES.ADMIN]: Object.values(FEATURES),
};

/** Roles that qualify for an artist profile (Artist Web) */
export const ARTIST_ROLES = [ROLES.SINGER, ROLES.RAPPER, ROLES.PRODUCER];

/** Human-readable role labels */
export const ROLE_LABELS = {
  [ROLES.SINGER]: 'Singer',
  [ROLES.RAPPER]: 'Rapper',
  [ROLES.PRODUCER]: 'Producer',
  [ROLES.VIDEO_EDITOR]: 'Video Editor',
  [ROLES.GRAPHIC_DESIGNER]: 'Graphic Designer',
  [ROLES.ADMIN]: 'Admin',
};

/** Role icon components */
export const ROLE_ICON_COMPONENTS = {
  [ROLES.SINGER]: Mic,
  [ROLES.RAPPER]: Mic2,
  [ROLES.PRODUCER]: Sliders,
  [ROLES.VIDEO_EDITOR]: Film,
  [ROLES.GRAPHIC_DESIGNER]: Palette,
  [ROLES.ADMIN]: Shield,
};

/** Role icons (emoji) */
export const ROLE_ICONS = {
  [ROLES.SINGER]: '🎤',
  [ROLES.RAPPER]: '🎙️',
  [ROLES.PRODUCER]: '🎹',
  [ROLES.VIDEO_EDITOR]: '🎬',
  [ROLES.GRAPHIC_DESIGNER]: '🎨',
};

/**
 * Get the union of features for a set of roles.
 * @param {string[]} roles - Array of role strings
 * @returns {string[]} Array of unique feature strings
 */
export function getFeaturesForRoles(roles) {
  if (!roles || roles.length === 0) return [];
  const featureSet = new Set();
  roles.forEach((role) => {
    const features = ROLE_FEATURES[role];
    if (features) {
      features.forEach((f) => featureSet.add(f));
    }
  });
  return Array.from(featureSet);
}

/**
 * Check if a set of roles grants access to a specific feature.
 * @param {string[]} roles
 * @param {string} feature
 * @returns {boolean}
 */
export function hasFeatureAccess(roles, feature) {
  return getFeaturesForRoles(roles).includes(feature);
}

/**
 * Check if any of the user's roles is an artist role.
 * @param {string[]} roles
 * @returns {boolean}
 */
export function isArtistRole(roles) {
  return roles.some((r) => ARTIST_ROLES.includes(r));
}

/**
 * Determine if a user's profile is complete and eligible for release submissions.
 * Requires:
 * - Existing profile record
 * - Explicitly completed onboarding (onboarding_completed !== false)
 * - Valid non-empty stage name
 * - At least one selected profession / creator role
 *
 * @param {Object} profile - User profile record from profiles table
 * @param {string[]} roles - Array of user creator roles
 * @returns {boolean}
 */
export function isProfileComplete(profile, roles) {
  if (!profile) return false;
  if (profile.onboarding_completed === false) return false;
  if (!profile.stage_name || typeof profile.stage_name !== 'string' || !profile.stage_name.trim()) {
    return false;
  }
  if (!roles || !Array.isArray(roles) || roles.length === 0) {
    return false;
  }
  return true;
}
